import {ChangeEvent, ReactElement, Suspense, useState} from 'react';
import {Box, Button, Skeleton, TextField} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {FinishId} from 'interfaces/Finish';
import {
  Cable,
  CablesPlacement as ICablesPlacement,
  ExtensionType,
  Section as SectionInterface,
  SectionCableId,
  SectionId,
  Tray as ITray
} from 'interfaces/Project';
import {System} from 'interfaces/System';
import CablesPlacement from './components/CablesPlacement';
import CopyCablesToSection from './components/CopyCablesToSection';
import FindTrays from './components/FindTrays';
import SectionCables from './components/SectionCables';
import SectionCard from './components/SectionCard';
import SectionDistance from './components/SectionDistance';
import SectionExtension from './components/SectionExtension/SectionExtension';
import SectionLength from './components/SectionLength';
import SelectFinish from './components/SelectFinish';
import SelectSystem from './components/SelectSystem';
import Tray from './components/Tray';

type Props = {
  section: SectionInterface;
  otherSections: SectionInterface[];
  systems: System[];
  onSystemChange: (systemId: number) => void;
  onFinishChange: (finishId: FinishId) => void;
  onCableSelect: (cable: Cable) => void;
  onCablesCopy: (to: SectionId) => void;
  onLengthChange: (length: number) => void;
  onDistanceChange: (distance: number) => void;
  onExtensionChange: (extension: number) => void;
  onExtensionTypeChange: (type: ExtensionType) => void;
  onNameChange: (name: string) => void;
  onNotesChange: (notes: string) => void;
  onTraySelect: (tray: ITray) => void;
  onCableUnitsChange: (sectionCableId: SectionCableId, units: number) => void;
  onCableDelete: (sectionCableId: SectionCableId) => void;
  onCablesPlacementChange: (placement: ICablesPlacement) => void;
  deleteTrayFromSection: () => void;
  onSaveProject: () => void;
};

export default function SectionView({
  section,
  otherSections,
  systems,
  onSystemChange,
  onFinishChange,
  onCableSelect,
  onCablesCopy,
  onLengthChange,
  onDistanceChange,
  onExtensionChange,
  onExtensionTypeChange,
  onNameChange,
  onNotesChange,
  onTraySelect,
  onCableUnitsChange,
  onCableDelete,
  onCablesPlacementChange,
  deleteTrayFromSection,
  onSaveProject
}: Props): ReactElement {
  const {t} = useTranslation('common');
  const [hasFinish, setHasFinish] = useState<boolean>(false);
  const hasSelectedTray = Boolean(section.tray);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: {xs: 3, sm: 1.5, md: 3},
        alignItems: 'flex-start'
      }}
    >
      <Box sx={{width: '100%'}}>
        <SectionCard title={t('common:sectionDetails')}>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 1}}>
              <Box sx={{flex: 1, minWidth: 0}}>
                <TextField
                  id="sectionName"
                  label={t('common:name')}
                  value={section.name}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    onNameChange(event.target.value)
                  }
                  fullWidth
                />
              </Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <SectionLength initialValue={section.length} t={t} onChange={onLengthChange} />
              </Box>
            </Box>
            <TextField
              id="sectionNotes"
              label={t('common:sectionDescription')}
              value={section.notes ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) => onNotesChange(event.target.value)}
              fullWidth
            />
          </Box>
        </SectionCard>
      </Box>

      <Box sx={{width: '100%'}}>
        <SectionCard
          title={t('common:cablesSelection')}
          action={
            otherSections.length > 0 ? (
              <CopyCablesToSection onCablesCopy={onCablesCopy} sections={otherSections} />
            ) : undefined
          }
        >
          <SectionCables
            deleteTrayFromSection={deleteTrayFromSection}
            hasSelectedTray={hasSelectedTray}
            sectionCables={section.cables}
            onCableUnitsChange={onCableUnitsChange}
            onCableDelete={onCableDelete}
            onCableSelect={onCableSelect}
          />
        </SectionCard>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: {xs: 'column', md: 'row'},
          gap: 1.5,
          alignItems: 'stretch',
          width: '100%'
        }}
      >
        <Box sx={{flex: 1, minWidth: 0}}>
          <SectionCard title={t('common:percentageExtension.label')} compact>
            <SectionExtension
              initialExtensionValue={section.extension}
              extensionType={section.extensionType}
              t={t}
              onValueChange={onExtensionChange}
              onExtensionTypeChange={onExtensionTypeChange}
              deleteTrayFromSection={deleteTrayFromSection}
              hasSelectedTray={hasSelectedTray}
            />
          </SectionCard>
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
          <SectionCard title={t('common:cablesPlacement')} compact>
            <CablesPlacement
              placement={section.cablesPlacement}
              deleteTrayFromSection={deleteTrayFromSection}
              hasSelectedTray={hasSelectedTray}
              onChange={onCablesPlacementChange}
            />
          </SectionCard>
        </Box>
      </Box>

      <Box sx={{width: '100%'}}>
        <SectionCard title={t('common:traySystemSelection')} titleSize={{xs: 20, sm: 24}}>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
            <SelectSystem
              systems={systems}
              systemId={section.traySystemId}
              onSystemChange={onSystemChange}
              hasSelectedTray={hasSelectedTray}
              deleteTrayFromSection={deleteTrayFromSection}
            />
            <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 1.5}}>
              <Box sx={{flex: 1, minWidth: 0, display: hasFinish ? 'block' : 'none'}}>
                <Suspense
                  fallback={<Skeleton height={56} variant="rectangular" sx={{borderRadius: 1}} />}
                >
                  <SelectFinish
                    setHasFinish={setHasFinish}
                    deleteTrayFromSection={deleteTrayFromSection}
                    finishId={section.finishId}
                    hasSelectedTray={hasSelectedTray}
                    onFinishChange={onFinishChange}
                    traySystemId={section.traySystemId}
                  />
                </Suspense>
              </Box>
              <Box sx={{flex: 1, minWidth: 0}}>
                <SectionDistance
                  deleteTrayFromSection={deleteTrayFromSection}
                  hasSelectedTray={hasSelectedTray}
                  initialValue={section.distance}
                  onChange={onDistanceChange}
                  t={t}
                />
              </Box>
            </Box>
            <FindTrays
              hasFinish={hasFinish}
              section={section}
              onSelectTray={onTraySelect}
              onSearch={deleteTrayFromSection}
            />
            {section.tray && <Tray section={section} tray={section.tray} />}
          </Box>
        </SectionCard>
      </Box>

      <Button
        variant="outlined"
        color="inherit"
        size="large"
        onClick={onSaveProject}
        sx={{color: 'ctaDark', borderColor: 'borderColorStrong'}}
      >
        {t('common:saveSection')}
      </Button>
    </Box>
  );
}
